// import React, { useRef, useEffect } from "react";
// import MessageInput, { MessageInputHandle } from "./MessageInput";
// import PromptSections from "../PromptSections/PromptSections";
// import styles from "./chatwindow.module.css";
// import MessageBubble from "./MessageBubble";
// import { useDispatch, useSelector } from "react-redux";
// import { RootState, AppDispatch } from "../../store";
// import { fetchPrompts } from "../../store/chatSlice";
// import { Link } from "react-router-dom";
// import LoadingSpinner from "../LoadingSpinner";
// import { useAuth } from "../../context/AuthContext";
// import { SAMPLE_PROMPTS } from "../../utils/constants";
// import ContentCard from "../ContentCard";
// import { useTheme } from "../../context/ThemeContext";
// import { useChat } from "../../hooks/UseChat";

// const ChatWindow = () => {
//   const dispatch = useDispatch<AppDispatch>();
//   const termsOfUse = import.meta.env.VITE_TERMS_OF_USE;
//   const {
//     isChatOpen,
//     messages,
//     prompts,
//     activeSessionId,
//     loadingSession,
//     loading,
//   } = useSelector((state: RootState) => state.chat);
//   const { getAccessToken } = useAuth();
//   const { drawerOpen, setDrawerOpen } = useChat();

//   useEffect(() => {
//     const loadPrompts = async () => {
//       try {
//         const token = await getAccessToken();
//         dispatch(fetchPrompts(token || undefined));
//       } catch (error) {
//         // Try to fetch without token as fallback
//         dispatch(fetchPrompts(undefined));
//       }
//     };
//     loadPrompts();
//   }, [dispatch]); // Only run once on mount

//   const hasMessages = messages && messages.length > 0;
//   const inputRef = useRef<MessageInputHandle>(null);
//   const { mode } = useTheme();

//   return (
//     <div className={styles.chatWindowContainer}>
//       {loadingSession ? (
//         <div className={styles.chatWindow}>
//           <LoadingSpinner size="lg" label="Loading session..." />
//         </div>
//       ) : hasMessages ? (
//         <MessageBubble
//           messages={messages}
//           agentAvatar="images/bot-avatar.svg"
//           sessionId={activeSessionId || undefined}
//           loading={loading}
//           onFollowupClick={(question) => inputRef.current?.sendPrompt(question)}
//           drawerOpen={drawerOpen}
//           setDrawerOpen={setDrawerOpen}
//         />
//       ) : (
//         <>
//           <div className={styles.chatHeader}>
//             <ContentCard
//               title="Knowledge Assistant"
//               imageUrl={`${mode === "dark" ? "images/logo_white.svg" : "images/logo.svg"}`}
//               subTitle=""
//               description="Created by AZ GPPM Platforms Team"
//             />
//           </div>
//         </>
//       )}
//       <div className={styles.inputContainer}>
//         <MessageInput ref={inputRef} />
//         {hasMessages && (
//           <div className={styles.disclaimer}>
//             AI-generated responses may be inaccurate or unreliable. Read the{" "}
//             <Link to={termsOfUse} target="_blank">
//               terms of use
//             </Link>
//             .
//           </div>
//         )}
//       </div>
//       {!hasMessages &&
//         (loading ? (
//           <div
//             style={{
//               display: "flex",
//               justifyContent: "center",
//               alignItems: "center",
//               padding: "32px",
//             }}
//           >
//             <LoadingSpinner size="md" label="Loading prompts..." />
//           </div>
//         ) : (
//           <PromptSections
//             prompts={prompts.length ? prompts : SAMPLE_PROMPTS}
//             handleClickPrompt={(p) => inputRef.current?.sendPrompt(p.title)}
//           />
//         ))}
//     </div>
//   );
// };

// export default React.memo(ChatWindow);
import React, { useRef, useEffect } from "react";
import MessageInput, { MessageInputHandle } from "./MessageInput";
import PromptSections from "../PromptSections/PromptSections";
import styles from "./chatwindow.module.css";
import MessageBubble from "./MessageBubble";
import { useDispatch, useSelector } from "react-redux";
import { RootState, AppDispatch } from "../../store";
import { fetchPrompts } from "../../store/chatSlice";
import { Link } from "react-router-dom";
import LoadingSpinner from "../LoadingSpinner";
import { useAuth } from "../../context/AuthContext";
import { SAMPLE_PROMPTS } from "../../utils/constants";
import ContentCard from "../ContentCard";
import { useTheme } from "../../context/ThemeContext";
import { useChat } from "../../hooks/UseChat";

const ChatWindow = () => {
  const dispatch = useDispatch<AppDispatch>();
  const termsOfUse = import.meta.env.VITE_TERMS_OF_USE;

  const {
    isChatOpen,
    messages,
    prompts,
    activeSessionId,
    loadingSession,
    loading,
  } = useSelector((state: RootState) => state.chat);

  const { getAccessToken } = useAuth();
  const { drawerOpen, setDrawerOpen } = useChat();
  const { mode } = useTheme();

  const inputRef = useRef<MessageInputHandle>(null);

  // ✅ Guard to prevent double API call in StrictMode
  const hasFetchedPromptsRef = useRef(false);

  useEffect(() => {
    if (hasFetchedPromptsRef.current) return;
    hasFetchedPromptsRef.current = true;

    const loadPrompts = async () => {
      try {
        const token = await getAccessToken();
        dispatch(fetchPrompts(token || undefined));
      } catch {
        dispatch(fetchPrompts(undefined));
      }
    };

    loadPrompts();
  }, [dispatch, getAccessToken]);

  const hasMessages = messages && messages.length > 0;

  return (
    <div className={styles.chatWindowContainer}>
      {loadingSession ? (
        <div className={styles.chatWindow}>
          <LoadingSpinner size="lg" label="Loading session..." />
        </div>
      ) : hasMessages ? (
        <MessageBubble
          messages={messages}
          agentAvatar="images/bot-avatar.svg"
          sessionId={activeSessionId || undefined}
          loading={loading}
          onFollowupClick={(question) =>
            inputRef.current?.sendPrompt(question)
          }
          drawerOpen={drawerOpen}
          setDrawerOpen={setDrawerOpen}
        />
      ) : (
        <>
          <div className={styles.chatHeader}>
            <ContentCard
              title="Knowledge Assistant"
              imageUrl={`${
                mode === "dark"
                  ? "images/logo_white.svg"
                  : "images/logo.svg"
              }`}
              subTitle=""
              description="Created by AZ GPPM Platforms Team"
            />
          </div>
        </>
      )}

      <div className={styles.inputContainer}>
        <MessageInput ref={inputRef} />
        {hasMessages && (
          <div className={styles.disclaimer}>
            AI-generated responses may be inaccurate or unreliable. Read the{" "}
            <Link to={termsOfUse} target="_blank">
              terms of use
            </Link>
            .
          </div>
        )}
      </div>

      {!hasMessages &&
        (loading ? (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              padding: "32px",
            }}
          >
            <LoadingSpinner size="md" label="Loading prompts..." />
          </div>
        ) : (
          <PromptSections
            prompts={prompts.length ? prompts : SAMPLE_PROMPTS}
            handleClickPrompt={(p) =>
              inputRef.current?.sendPrompt(p.title)
            }
          />
        ))}
    </div>
  );
};

export default React.memo(ChatWindow);
